import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import axiosInstance from '../../../api/axiosInstance'
import { trasladosService } from '../../../api/trasladosService'
import { FLEXY_PRODUCTS_MULTIPLE_PATH } from '../../../constants/productsApiPaths'
import type { CreateTrasladoPayload, TrasladoLinea } from '../../../types/traslados'
import type { Product } from '../../../types/orderFormTypes'
import { extractAxiosApiErrorMessage } from '../../../utils/apiErrorMessage'
import { isProductInactiveInSearch } from '../../../utils/productSearchUtils'
import {
  getCombinedStockDisplayOptions,
  mapApiProductToOrderDetails
} from '../../../utils/productStockDisplayUtils'
import { useProductSearch } from '../../../hooks/useProductSearch'
import { useAuth } from '../../../contexts/AuthContext'
import { validateCreateTrasladoPayload } from '../traslados/trasladoValidation'

export function useTrasladoForm() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [idalmacenOrigen, setIdalmacenOrigen] = useState<number | ''>('')
  const [idalmacenDestino, setIdalmacenDestino] = useState<number | ''>('')
  const [glosa, setGlosa] = useState('')
  const [lineas, setLineas] = useState<TrasladoLinea[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [productDetails, setProductDetails] = useState<Record<string, Record<string, unknown>>>({})
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({})

  const lastLoadedSkusRef = useRef('')
  const isLoadingRef = useRef(false)
  const stockDisplayOptions = getCombinedStockDisplayOptions()

  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    clearSearch
  } = useProductSearch()

  const loadProductDetails = useCallback(async (skus: string[]) => {
    const uniqueSkus = Array.from(new Set(skus.filter(Boolean)))
    if (!uniqueSkus.length) {
      setProductDetails({})
      setLoadingDetails({})
      lastLoadedSkusRef.current = ''
      return
    }

    const skusKey = uniqueSkus.sort().join(',')
    if (isLoadingRef.current || lastLoadedSkusRef.current === skusKey) return

    isLoadingRef.current = true
    lastLoadedSkusRef.current = skusKey

    const loadingMap: Record<string, boolean> = {}
    uniqueSkus.forEach((sku) => {
      loadingMap[sku] = true
    })
    setLoadingDetails(loadingMap)

    try {
      const response = await axiosInstance.get(
        `${FLEXY_PRODUCTS_MULTIPLE_PATH}?skus=${uniqueSkus.join(',')}&SinSaldo=true`
      )
      const products = Array.isArray(response.data?.products)
        ? response.data.products
        : Array.isArray(response.data)
          ? response.data
          : []

      const details: Record<string, Record<string, unknown>> = {}
      products.forEach((product: Record<string, unknown>) => {
        const mapped = mapApiProductToOrderDetails(product, stockDisplayOptions)
        const sku = String(mapped.sku ?? '').trim()
        if (sku) details[sku] = mapped
      })

      setProductDetails(details)
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al consultar stock de productos'))
      setProductDetails({})
    } finally {
      setLoadingDetails({})
      isLoadingRef.current = false
    }
  }, [stockDisplayOptions])

  useEffect(() => {
    const skus = lineas.map((linea) => linea.sku.trim()).filter(Boolean)
    loadProductDetails(skus)
  }, [lineas, loadProductDetails])

  const addProduct = useCallback((product: Product) => {
    if (isProductInactiveInSearch(product)) {
      toast.error('Este producto está inactivo y no se puede agregar', { icon: '⚠️' })
      return
    }

    setLineas((current) => {
      const index = current.findIndex((linea) => linea.sku === product.sku)
      if (index >= 0) {
        return current.map((linea, i) =>
          i === index ? { ...linea, cantidad: linea.cantidad + 1 } : linea
        )
      }
      return [
        ...current,
        { sku: product.sku, producto: product.name, cantidad: 1 }
      ]
    })

    clearSearch()
    setShowResults(false)
  }, [clearSearch, setShowResults])

  const submit = useCallback(async () => {
    const usuarioCreacion = user?.email?.trim() ?? ''
    if (!usuarioCreacion) {
      toast.error('No se pudo obtener el correo de la sesión')
      return
    }

    const payload: CreateTrasladoPayload = {
      idalmacen_origen: Number(idalmacenOrigen),
      idalmacen_destino: Number(idalmacenDestino),
      glosa: glosa.trim(),
      usuario_creacion: usuarioCreacion,
      lineas: lineas.map((linea) => ({
        sku: linea.sku.trim(),
        producto: linea.producto.trim(),
        cantidad: Number(linea.cantidad)
      }))
    }

    const validation = validateCreateTrasladoPayload(payload)
    if (!validation.valid) {
      validation.errors.forEach((message) => toast.error(message))
      return
    }

    setIsSubmitting(true)
    try {
      await trasladosService.createTraslado(payload)
      toast.success('Transferencia creada correctamente')
      navigate('/flexy/traslados')
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al crear la transferencia'))
    } finally {
      setIsSubmitting(false)
    }
  }, [glosa, idalmacenDestino, idalmacenOrigen, lineas, navigate, user?.email])

  return {
    idalmacenOrigen,
    setIdalmacenOrigen,
    idalmacenDestino,
    setIdalmacenDestino,
    glosa,
    setGlosa,
    lineas,
    setLineas,
    isSubmitting,
    productDetails,
    loadingDetails,
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    addProduct,
    submit
  }
}
